import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";

const fmt = (v, t) => {
  if (t === "date") return v ? formatDeShort(v) : "—";
  if (t === "num") return Number(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });
  if (t === "eur") return Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v;
};

function BillingAction({ a, onChanged }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  if (a.external) return (
    <span className="inline-flex items-center gap-2 text-[11px] text-emerald-700" data-testid={`ext-billed-${a.client_id}`}>
      extern abgerechnet {formatDeShort(a.external.at)}
      <button disabled={busy} onClick={() => window.confirm("Externe Abrechnung zurücknehmen?") && run(() => api.delete(`/clients/${a.client_id}/external-billing`), "Zurückgenommen")}
        className="underline text-slate-500 hover:text-slate-900" data-testid={`ext-undo-${a.client_id}`}>rückgängig</button>
    </span>
  );
  if (!(a.open_ue > 0)) return <span className="text-[11px] text-slate-400">{a.total_ue > 0 ? "vollständig" : "—"}</span>;
  return (
    <button disabled={busy} onClick={() => run(() => api.post(`/clients/${a.client_id}/external-billing`), `${fmt(a.open_ue, "num")} UE als extern abgerechnet bestätigt`)}
      className="border border-slate-300 px-2 py-0.5 text-[11px] hover:border-slate-900 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap" data-testid={`ext-confirm-${a.client_id}`}>
      Extern abgerechnet ({fmt(a.open_ue, "num")} UE)
    </button>
  );
}

function Section({ s, idx, onNavigate, onChanged }) {
  return (
    <div className="mb-8" data-testid={`kpi-detail-section-${idx}`}>
      <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2">{s.title}</div>
      <div className="flex flex-wrap gap-2 mb-3" data-testid={`kpi-detail-totals-${idx}`}>
        {Object.entries(s.totals).map(([k, v]) => (
          <span key={k} className="border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs"><span className="text-slate-500">{k}:</span> <b className="font-mono text-slate-900">{fmt(v, k.includes("€") ? "eur" : "num")}</b></span>
        ))}
      </div>
      {s.rows.length === 0 ? <div className="text-xs text-slate-400 italic">Keine Einträge.</div> : (
        <div className="border border-slate-200 overflow-x-auto max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0"><tr>{s.columns.map((c, i) => <th key={c} className={`px-3 py-2 text-[10px] font-bold uppercase tracking-overline text-slate-500 ${["num", "eur"].includes(s.types[i]) ? "text-right" : "text-left"}`}>{c}</th>)}{s.actions && <th className="px-3 py-2 text-[10px] font-bold uppercase tracking-overline text-slate-500 text-left">Abrechnung</th>}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {s.rows.map((r, ri) => (
                <tr key={ri} className="hover:bg-slate-50" data-testid={`kpi-detail-row-${idx}-${ri}`}>
                  {r.cells.map((v, ci) => (
                    <td key={ci} className={`px-3 py-2 whitespace-nowrap ${["num", "eur"].includes(s.types[ci]) ? "text-right font-mono" : ""}`}>
                      {ci === 0 && r.link ? <Link to={r.link} onClick={onNavigate} className="text-slate-900 underline-offset-4 hover:underline">{fmt(v, s.types[ci])}</Link> : fmt(v, s.types[ci])}
                    </td>
                  ))}
                  {s.actions && <td className="px-3 py-2">{r.action && <BillingAction a={r.action} onChanged={onChanged} />}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function KpiDetailDialog({ kpi, label, month, monthName, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/team-dashboard/details", { params: { kpi, month } }).then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => {
    if (!kpi) return;
    setD(null);
    load();
  }, [kpi, month]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={!!kpi} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="kpi-detail-dialog">
        <DialogHeader><DialogTitle>{label} · {monthName}</DialogTitle></DialogHeader>
        {d === null && <div className="text-xs text-slate-400 py-6">Lädt…</div>}
        {d === false && <div className="text-sm text-red-600 py-6">Details konnten nicht geladen werden.</div>}
        {d && d.sections.map((s, i) => <Section key={i} s={s} idx={i} onNavigate={onClose} onChanged={() => { load(); onChanged && onChanged(); }} />)}
      </DialogContent>
    </Dialog>
  );
}
